import { Container, Drawer as MuiDrawer, styled } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useGetElementSize } from '../../hooks/useGetElementSize';
import { selectAppStatus, setDrawerOpen } from '../../redux/reducers/appStatusReducer';
import {
  selectConversations,
  setSearchQuery as setConversationsSearchQuery,
} from '../../redux/reducers/conversationReducer';
import {
  selectFolders,
  setSearchQuery as setFoldersSearchQuery,
} from '../../redux/reducers/folderReducer';
import { AppDispatch } from '../../redux/store';
import { AppEnum } from '../../Types/enums';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';
import DrawerApps from './DrawerApps';
import DrawerChatList from './DrawerChatList';
import DrawerFolderList from './DrawerFolderList';
import DrawerPrimaryButtons from './DrawerPrimaryButtons';
import DrawerSearch from './DrawerSearch';

type NavDrawerContainerProps = {
  open: boolean;
  drawerWidth: string;
};

// We need this because of the children that this container wraps
const NavDrawerContainer = styled(Container, {
  shouldForwardProp: (prop) => !['open', 'drawerWidth'].includes(prop as string),
})<NavDrawerContainerProps>(({ open, drawerWidth }) => ({
  overflowY: 'hidden',
  backgroundColor: 'white',
  height: 'calc(100vh - var(--banner-height, 0px))',
  width: drawerWidth,
  minWidth: drawerWidth,
  transition: 'width 0.2s ease-in-out, min-width 0.2s ease-in-out',
  padding: open ? '20px 7px 0px' : '20px 0px 0px',
}));

interface Props {
  children?: React.ReactNode;
}

const NavDrawer: React.FC<Props> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { conversations } = useSelector(selectConversations);

  const { selectedApp, drawerOpen, drawerWidth, folderListOpen, recentChatsOpen } =
    useSelector(selectAppStatus);
  const { pinnedFolders, unpinnedFolders, totalCount } = useSelector(selectFolders);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const shouldScrollRef = useRef<boolean>(false);
  const analytics = useAnalytics();
  const location = useLocation();
  const { height: buttonContainerHeight } = useGetElementSize('drawer-primary-buttons');

  const [bannerHeight, setBannerHeight] = useState<number>(
    parseInt(document.documentElement.style.getPropertyValue('--banner-height') || '0', 10) || 0
  );
  const [remainingFolderListHeight, setRemainingFolderListHeight] = useState<string>('0px');
  const [remainingChatListHeight, setRemainingChatListHeight] = useState<string>('0px');

  useEffect(() => {
    const handleBannerHeightChange = () => {
      setBannerHeight(
        parseInt(document.documentElement.style.getPropertyValue('--banner-height') || '0', 10) || 0
      );
    };

    // Listen for window resize
    window.addEventListener('resize', handleBannerHeightChange);

    // Listen for changes to the style attribute (CSS variable changes)
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          handleBannerHeightChange();
        }
      }
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });

    // Initial set in case the variable changes before mount
    handleBannerHeightChange();

    return () => {
      window.removeEventListener('resize', handleBannerHeightChange);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    // Calculate the total remaining height after subtracting fixed elements
    const vh = window.innerHeight;
    const appSelectorHeight = 72;
    const closedAccordionHeight = 48;
    const searchBarHeight = location.pathname.includes('search') ? 0 : 60;
    const baseHeight =
      vh -
      bannerHeight -
      appSelectorHeight -
      buttonContainerHeight -
      searchBarHeight -
      closedAccordionHeight * 2 -
      //accordion gap
      12;
    const halfHeight = Math.floor(baseHeight / 2);

    const loadMoreHeight = 45;

    // calculate content heights
    const folderListItemHeight = 41;
    const folderContentHeight =
      folderListItemHeight * (pinnedFolders.length + unpinnedFolders.length) +
      (pinnedFolders.length + unpinnedFolders.length < totalCount ? loadMoreHeight : 0);

    const chatListItemHeight = 105;
    const chatContentHeight = chatListItemHeight * conversations.length + loadMoreHeight;

    // default to the size of the closed accordion
    let newFolderListHeight = '0px';
    let newChatListHeight = '0px';

    // if only one accordion is open, let it take up all the space
    if (!folderListOpen) {
      newChatListHeight = `calc(min(${baseHeight}px, ${chatContentHeight}px))`;
    } else if (!recentChatsOpen) {
      newFolderListHeight = `calc(min(${baseHeight}px, ${folderContentHeight}px))`;
    }
    // if both accordions are open, split the space
    else {
      // if both contents exceed half the space, split evenly
      if (folderContentHeight > halfHeight && chatContentHeight > halfHeight) {
        newFolderListHeight = `${halfHeight}px`;
        newChatListHeight = `${halfHeight}px`;
      } else {
        // if one content is less than half, give the rest to the other
        if (folderContentHeight <= halfHeight && chatContentHeight >= halfHeight) {
          newFolderListHeight = `${folderContentHeight}px`;
          newChatListHeight = `calc(min(${
            baseHeight - folderContentHeight
          }px, ${chatContentHeight}px))`;
        } else if (folderContentHeight >= halfHeight && chatContentHeight <= halfHeight) {
          newFolderListHeight = `calc(min(${
            baseHeight - chatContentHeight
          }px, ${folderContentHeight}px))`;
          newChatListHeight = `${chatContentHeight}px`;
        } else {
          newFolderListHeight = `calc(min(${halfHeight}px, ${folderContentHeight}px))`;
          newChatListHeight = `calc(min(${halfHeight}px, ${chatContentHeight}px))`;
        }
      }
    }

    setRemainingFolderListHeight(newFolderListHeight);
    setRemainingChatListHeight(newChatListHeight);
  }, [
    buttonContainerHeight,
    location.pathname,
    folderListOpen,
    recentChatsOpen,
    bannerHeight,
    pinnedFolders,
    unpinnedFolders,
    conversations,
    totalCount,
  ]);

  const handleDrawerToggle = () => {
    analytics.event(
      AnalyticsEvent.WM_NIGEL_OPEN_CLOSE_LEFT_PANEL,
      {
        value: (!drawerOpen).toString(),
      },
      {
        leftPanelWasOpen: drawerOpen,
        leftPanelNowOpen: !drawerOpen,
      }
    );

    dispatch(setFoldersSearchQuery(''));
    dispatch(setConversationsSearchQuery(''));
    dispatch(setDrawerOpen(!drawerOpen));

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  };

  useEffect(() => {
    const adjustScroll = () => {
      if (shouldScrollRef.current && containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
        shouldScrollRef.current = false;
      }
    };

    adjustScroll();
  }, [conversations, dispatch]);

  return (
    <div style={{ width: drawerWidth, zIndex: 1, transition: 'width 0.2s ease-in-out' }}>
      <MuiDrawer variant="permanent" anchor="left" sx={{ height: '100%', width: drawerWidth }}>
        <NavDrawerContainer
          open={drawerOpen}
          drawerWidth={drawerWidth}
          ref={containerRef}
          style={{
            paddingLeft: '5px',
            paddingRight: '5px',
            backgroundColor: '#F9F9F9',
          }}
        >
          <DrawerApps handleDrawerToggle={handleDrawerToggle} />
          {selectedApp !== AppEnum.INTELLIO_INSIGHTS && (
            <>
              <DrawerPrimaryButtons showMyFolderButton={selectedApp !== AppEnum.DEAL_GPT} />
              <DrawerSearch />
              <div>
                <div>
                  <DrawerFolderList folderListHeight={remainingFolderListHeight} />
                  <DrawerChatList chatListHeight={remainingChatListHeight} />
                </div>
                {children}
              </div>
            </>
          )}
        </NavDrawerContainer>
      </MuiDrawer>
    </div>
  );
};

export default NavDrawer;
